"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Globe, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/ui/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/ui/components/ui/sheet";
import { useIsMdUp } from "@/hooks/use-is-md-up";
import { useHeaderScrollHideSuppression } from "@/ui/components/header-scroll-shadow";
import { useLocaleChannelState } from "./use-locale-channel-state";
import { LocaleChannelAccordion } from "./locale-channel-accordion";

const triggerClass =
	"inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function LocaleChannelTrigger() {
	const t = useTranslations("localeChannel");
	const isMdUp = useIsMdUp();
	const [open, setOpen] = useState(false);
	const setSuppressHeaderScrollHide = useHeaderScrollHideSuppression();
	const { currentLocale, currentChannel, referenceCurrency } = useLocaleChannelState();

	useEffect(() => {
		return () => setSuppressHeaderScrollHide(false);
	}, [setSuppressHeaderScrollHide]);

	const handleOpenChange = (value: boolean) => {
		setOpen(value);
		setSuppressHeaderScrollHide(value);
	};
	const close = () => handleOpenChange(false);

	// Every flex child down to the scrolling list must be allowed to shrink.
	const content = (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
			<LocaleChannelAccordion
				currentLocale={currentLocale}
				currentChannel={currentChannel}
				currentCurrency={referenceCurrency}
				onClose={close}
			/>
		</div>
	);
	const closeButton = (
		<button
			type="button"
			onClick={close}
			aria-label={t("close")}
			className="absolute right-3 top-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			<X className="h-4 w-4" aria-hidden />
		</button>
	);

	if (isMdUp) {
		return (
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<button type="button" className={triggerClass} aria-label={t("triggerAria")}>
						<Globe className="h-5 w-5" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					side="bottom"
					sideOffset={8}
					collisionPadding={16}
					aria-label={t("title")}
					className="relative flex max-h-[min(85dvh,40rem,var(--radix-popover-content-available-height))] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl p-0 shadow-elevated"
				>
					{content}
					{closeButton}
				</PopoverContent>
			</Popover>
		);
	}

	return (
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetTrigger asChild>
				<button type="button" className={triggerClass} aria-label={t("triggerAria")}>
					<Globe className="h-5 w-5" />
				</button>
			</SheetTrigger>
			<SheetContent
				side="bottom"
				aria-describedby={undefined}
				className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden rounded-t-2xl border-t p-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
			>
				<SheetTitle className="sr-only">{t("title")}</SheetTitle>
				{content}
				{closeButton}
			</SheetContent>
		</Sheet>
	);
}
